import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/lib/notifications';
import { authService } from '@/lib/auth';

export function useNotificationRefresh() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const isRegistered = useRef(false);

  useEffect(() => {
    // Only register the callback once to avoid multiple callbacks
    if (!isRegistered.current) {
      console.log('Registering message refresh notification callback');
      const unsubscribe = notificationService.onMessageReceived(async (data) => {
        console.log('Notification received, checking if we should refresh messages:', data);
        
        // Check if user is authenticated before processing
        const isAuthenticated = await authService.isSessionValid();
        if (!isAuthenticated) {
          console.log('User not authenticated, ignoring notification refresh');
          return;
        }

        const category = data?.notification_category as string | undefined;
        const kind = (data?.notification_kind ?? data?.type) as string | undefined;
        const isLeaveNotification = category === "leave" || kind?.startsWith("leave_");
        if (isLeaveNotification) {
          queryClient.invalidateQueries({ queryKey: ["leave", "list"] });
          queryClient.invalidateQueries({ queryKey: ["shifts", "scheduled"] });
        }
        
        // Check if we're on a page that shows messages
        const currentPath = window.location.pathname;
        const isMessagesPage = currentPath === '/messages';
        const isTaskDetailsPage = currentPath.startsWith('/tasks/') && currentPath !== '/tasks';
        
        if (isMessagesPage || isTaskDetailsPage) {
          console.log('On messages page, refreshing messages...');
          
          if (isMessagesPage) {
            // Refresh general messages
            queryClient.invalidateQueries({ queryKey: ['messages'] });
          } else if (isTaskDetailsPage) {
            // Extract taskId from path and refresh task-specific messages
            const taskIdMatch = currentPath.match(/\/tasks\/(\d+)/);
            if (taskIdMatch) {
              const taskId = parseInt(taskIdMatch[1], 10);
              console.log('Refreshing messages for task:', taskId);
              queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'messages'] });
            }
          }
        }
      });
      
      isRegistered.current = true;
      
      // Cleanup on unmount
      return () => {
        console.log('Unregistering message refresh notification callback');
        unsubscribe();
        isRegistered.current = false;
      };
    }
  }, [queryClient]); // Remove location.pathname dependency to prevent re-registration
}
